import {
    Component,
    ElementRef,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { from, of } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';

import { Column, Row } from '../../coaching-observation/experimental.grid';
import {
    PopUpComponent,
    PopUpService,
} from '../../coaching-observation/experimental.pop-up';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { TranslationKeyMap } from '../coaching-insight.i18n';
import { RosterType } from '../coverage.coaching-insight.model';
import { CalendarComponent } from '../schedule/calendar/calendar.component';
import {
    EventParticipant,
    ScheduleEvent,
} from '../schedule/models/schedule-event.model';
import { ScheduleEventContainerComponent } from '../schedule/schedule-event/schedule-event.container.component';

import {
    CellContext,
    OtherScheduleItemsClickEvent,
    PeriodClickEvent,
    RowContext,
} from './coaching-coverage-grid.model';
import {
    gridShiftPeriodDateRange,
    gridSort,
    gridTogglePeriodColumn,
    gridTogglePeriodDateRangeSelect,
    gridToggleRowExpand,
    gridToggleScheduleForUser,
    gridToggleShowScheduleItemsList,
    rootChangeRosterType,
    rootReload,
    rootSetPeriodRange,
    rootShiftPeriodRange,
    rootToggleCreateEvent,
    rootTogglePeriodRangeSelect,
} from './coaching-coverage.action';
import { CoachingCoverageContext } from './coaching-coverage.model';
import coachingCoverageReducer from './coaching-coverage.reducer';
import { RootState } from './coaching-coverage.state';
import { ScheduleItemsListComponent } from './schedule-items-list.component';
import {
    DateRange,
    SelectPeriodDateRangeComponent,
} from './select-period-date-range.component';
import { SelectPeriodRangeComponent } from './select-period-range.component';

@Component({
    selector: '[cmpsCoachingCoverage]',
    templateUrl: './coaching-coverage.component.html',
    host: {
        'data-e2e': 'component-coaching-coverage',
    },
})
@UseStore<RootState>(
    'coachingInsight.coachingCoverage',
    coachingCoverageReducer.coachingCoverage,
)
export class CoachingCoverageComponent
    implements OnChanges, OnInit, OnDestroy, StateToProps<RootState>
{
    state: RootState;

    /** @private */ _i18n = TranslationKeyMap;
    @Input() context: CoachingCoverageContext;
    private _ref1: PopUpComponent<SelectPeriodRangeComponent>;
    private _ref2: PopUpComponent<SelectPeriodDateRangeComponent>;
    private _ref3: PopUpComponent<ScheduleItemsListComponent>;

    private _modalCalendarRef: NgbModalRef;

    private _eventRef: PopUpComponent<ScheduleEventContainerComponent>;

    constructor(
        private readonly _el: ElementRef,
        private readonly _injector: Injector,
        private readonly _modalService: NgbModal,
        private readonly _popUpService: PopUpService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) {
            return;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('context') && this.context?.user != null) {
            // context is not written to state
            useDispatcher(this, rootReload({ context: this.context }));
        }
    }

    ngOnInit(): void {
        if (this.context?.user != null) {
            useDispatcher(this, rootReload({ context: this.context }));
        }
    }

    ngOnDestroy(): void {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._ref1 && this._ref1.dismiss();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._ref2 && this._ref2.dismiss();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._ref3 && this._ref3.dismiss();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._eventRef && this._eventRef.close();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._modalCalendarRef && this._modalCalendarRef.dismiss();
    }

    stateToProps(prevState?: RootState): void {
        if (prevState.createEventContext !== this.state.createEventContext) {
            this._createEvent();
        }

        if (
            prevState.selectPeriodRangeContext !==
            this.state.selectPeriodRangeContext
        ) {
            this._selectPeriodRange();
        }

        if (
            prevState.scheduleForUserContext !==
            this.state.scheduleForUserContext
        ) {
            this._openScheduleForUser();
        }

        if (
            prevState.selectPeriodDateRangeContext !==
            this.state.selectPeriodDateRangeContext
        ) {
            this._selectPeriodDateRange();
        }

        if (
            prevState.scheduleItemsListContext !==
            this.state.scheduleItemsListContext
        ) {
            this._showScheduleItemsList();
        }
    }

    /** @private */ _onCreateEventClick(
        $event: MouseEvent | KeyboardEvent,
    ): void {
        $event.stopPropagation();
        useDispatcher(
            this,
            rootToggleCreateEvent({ userId: this.context.user.id }),
        );
    }

    /** @private */ _onPeriodClick(): void {
        useDispatcher(this, rootTogglePeriodRangeSelect({}));
    }

    /** @private */ _onPreviousPeriodClick(): void {
        useDispatcher(
            this,
            rootShiftPeriodRange({ context: this.context, forward: false }),
        );
    }

    /** @private */ _onNextPeriodClick(): void {
        useDispatcher(
            this,
            rootShiftPeriodRange({ context: this.context, forward: true }),
        );
    }

    /** @private */ _onScheduleForUserClick(user: EventParticipant): void {
        useDispatcher(
            this,
            gridToggleScheduleForUser({
                managerId: this.context.user.id,
                userId: null,
                user,
            }),
        );
    }

    /** @private */ _onSortChange($event: Column): void {
        useDispatcher(this, gridSort({ column: $event }));
    }

    /** @private */ _onTogglePeriodColumnClick($event: Column): void {
        useDispatcher(this, gridTogglePeriodColumn({ column: $event }));
    }

    /** @private */ _onToggleRowExpandClick(
        $event: Row<RowContext, CellContext>,
    ): void {
        useDispatcher(this, gridToggleRowExpand({ row: $event }));
    }

    /** @private */ _onGridPeriodClick($event: PeriodClickEvent): void {
        useDispatcher(
            this,
            gridTogglePeriodDateRangeSelect({
                column: $event.column,
                elementSelector: $event.elementSelector,
            }),
        );
    }

    /** @private */ _onRosterTypeChange($event: RosterType): void {
        useDispatcher(
            this,
            rootChangeRosterType({ context: this.context, rosterType: $event }),
        );
    }

    /** @private */ _onOtherScheduleItemsClick(
        $event: OtherScheduleItemsClickEvent,
    ): void {
        useDispatcher(
            this,
            gridToggleShowScheduleItemsList({
                cell: $event.cell,
                eventType: $event.type,
                elementSelector: $event.elementSelector,
            }),
        );
    }

    private _openScheduleForUser(): void {
        if (!this.state.scheduleForUserContext) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._modalCalendarRef && this._modalCalendarRef.dismiss();
        this._modalCalendarRef = this._modalService.open(CalendarComponent, {
            injector: this._injector,
            windowClass: 'cmps-calendar-modal',
        });

        const component = this._modalCalendarRef
            .componentInstance as CalendarComponent;
        component.managerId = this.state.scheduleForUserContext.managerId;
        component.performerId = this.state.scheduleForUserContext.performerId;
        component.performerUser =
            this.state.scheduleForUserContext.performerUser;
        // TODO: remove in case don't need to execute another process after close modal
        from(this._modalCalendarRef.result)
            .pipe(
                finalize(() => {
                    // if (this.state.scheduleForUserContext) {
                    useDispatcher(
                        this,
                        gridToggleScheduleForUser({
                            managerId: null,
                            userId: null,
                            user: null,
                        }),
                    );
                    // }
                    this._modalCalendarRef = null;
                }),
            )
            .subscribe({ error: () => null });
    }

    private _selectPeriodRange(): void {
        if (!this.state.selectPeriodRangeContext && this._ref1) {
            this._ref1.dismiss();
            return;
        }
        if (this._ref1) {
            this._ref1.componentRef.instance.selection =
                this.state.selectPeriodRangeContext.selection;
            return;
        }
        const ref = (this._ref1 = this._popUpService.show(
            SelectPeriodRangeComponent,
            {
                target: () =>
                    this._el.nativeElement.querySelector(
                        '[data-cc="period-range"]',
                    ),
                placement: 'bottom-auto',
                autoClose: true,
            },
        ));
        let selected: number;
        ref.componentRef.instance.selection =
            this.state.selectPeriodRangeContext.selection;
        ref.componentRef.instance.selectionChange
            .pipe(
                tap((next) => {
                    selected = next;
                    ref.close();
                }),
                catchError(() => of(null)),
                finalize(() => {
                    // if (this.state.selectPeriodRangeContext) {
                    useDispatcher(this, rootTogglePeriodRangeSelect({}));
                    // }
                    this._ref1 = null;
                }),
                takeUntil(from(ref.result)),
            )
            .subscribe({
                error: () => null,
                complete: () => {
                    if (
                        this.state.selectPeriodRangeContext &&
                        selected &&
                        selected !==
                            this.state.selectPeriodRangeContext.selection
                    ) {
                        useDispatcher(
                            this,
                            rootSetPeriodRange({
                                context: this.context,
                                maxShown: selected,
                            }),
                        );
                    }
                },
            });
    }

    private _selectPeriodDateRange(): void {
        if (!this.state.selectPeriodDateRangeContext && this._ref2) {
            this._ref2.dismiss();
            return;
        }
        if (this._ref2) {
            this._ref2.props.target = () =>
                this._el.nativeElement.querySelector(
                    this.state.selectPeriodDateRangeContext.elementSelector,
                );
            this._ref2.componentRef.instance.selection =
                this.state.selectPeriodDateRangeContext.selection;
            return;
        }
        const ref = (this._ref2 = this._popUpService.show(
            SelectPeriodDateRangeComponent,
            {
                target: () =>
                    this._el.nativeElement.querySelector(
                        this.state.selectPeriodDateRangeContext.elementSelector,
                    ),
                placement: 'bottom-auto',
                backdrop: true,
                backdropClass: 'coaching-coverage-grid-pu-backdrop',
                relativeTo: () =>
                    this._el.nativeElement.querySelector('[wrapper][grid]'),
                autoClose: true,
            },
        ));
        let selected: DateRange;
        ref.componentRef.instance.dateRanges =
            this.state.selectPeriodDateRangeContext.dateRanges;
        ref.componentRef.instance.selection =
            this.state.selectPeriodDateRangeContext.selection;
        ref.componentRef.instance.selectionChange
            .pipe(
                tap((next) => {
                    selected = next;
                    ref.close();
                }),
                catchError(() => of(null)),
                finalize(() => {
                    // if (this.state.selectPeriodDateRangeContext) {
                    useDispatcher(
                        this,
                        gridTogglePeriodDateRangeSelect({
                            column: null,
                            elementSelector: null,
                        }),
                    );
                    // }
                    this._ref2 = null;
                }),
                takeUntil(from(ref.result)),
            )
            .subscribe({
                error: () => null,
                complete: () => {
                    if (
                        this.state.selectPeriodDateRangeContext &&
                        selected &&
                        selected !==
                            this.state.selectPeriodDateRangeContext.selection
                    ) {
                        useDispatcher(
                            this,
                            gridShiftPeriodDateRange({
                                context: this.context,
                                column: this.state.selectPeriodDateRangeContext
                                    .column,
                                dateRange: selected,
                            }),
                        );
                    }
                },
            });
    }

    private _createEvent(): void {
        if (!this.state.createEventContext) {
            return;
        }
        this._eventRef = this._popUpService.show(
            ScheduleEventContainerComponent,
            {
                target: () =>
                    this._el.nativeElement.querySelector('.cmps-button'),
                placement: 'bottom-left',
                containerClass: 'event-pu-container',
            },
        );
        this._eventRef.componentRef.instance.userId =
            this.state.createEventContext.userId;
        this._eventRef.componentRef.instance.closed
            .pipe(
                catchError(() => of(null)),
                tap((savedevent: ScheduleEvent) => {
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    if (savedevent && savedevent.eventId) {
                        useDispatcher(
                            this,
                            rootReload({ context: this.context }),
                        );
                    }
                }),
                finalize(() => {
                    // if (this.state.createEventContext) {
                    useDispatcher(
                        this,
                        rootToggleCreateEvent({ userId: null }),
                    );
                    // }
                }),
                takeUntil(from(this._eventRef.result)),
            )
            .subscribe({
                next: () => this._eventRef.close(),
                error: () => null,
            });
    }

    private _showScheduleItemsList(): void {
        const ctx = this.state.scheduleItemsListContext;
        if (!ctx && this._ref3) {
            this._ref3.dismiss();
            return;
        }
        if (this._ref3) {
            this._ref3.props.target = () =>
                this._el.nativeElement.querySelector(ctx.elementSelector);
            this._ref3.componentRef.instance.schedule = ctx.schedule;
            return;
        }
        const ref = (this._ref3 = this._popUpService.show(
            ScheduleItemsListComponent,
            {
                target: () =>
                    this._el.nativeElement.querySelector(ctx.elementSelector),
                placement: 'bottom-left',
                backdrop: true,
                backdropClass: 'coaching-coverage-grid-pu-backdrop',
                relativeTo: () =>
                    this._el.nativeElement.querySelector('[wrapper][grid]'),
                autoClose: true,
            },
        ));
        ref.componentRef.instance.schedule = ctx.schedule;
        from(ref.result)
            .pipe(
                catchError(() => of(null)),
                finalize(() => {
                    useDispatcher(
                        this,
                        gridToggleShowScheduleItemsList({
                            cell: null,
                            eventType: null,
                            elementSelector: null,
                        }),
                    );
                    this._ref3 = null;
                }),
            )
            .subscribe({ error: () => null });
    }
}
